import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FeatureManagementService } from '../../../management/feature-management/feature-management.service';
import { CommonService } from '../../../../shared/services/common.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomerService } from '../../customer.service';
import { forkJoin } from 'rxjs';
import { ApplicationContextService } from '../../../../shared/services/application-context.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-customer-features',
    templateUrl: './customer-features.component.html',
    styleUrls: ['./customer-features.component.scss']
})
export class CustomerFeaturesComponent implements OnInit {
    customerId: string = '';
    features: any[] = [];
    // Features for which this customer has an active configuration
    activeFeatureIds: Set<number> = new Set<number>();
    isLoading: boolean = false;
    isFeatureModalVisible: boolean = false;
    isFeatureFormVisible: boolean = false;
    selectedFeature: any = null;

    constructor(
        private route: ActivatedRoute,
        private featureManagementService: FeatureManagementService,
        private commonService: CommonService,
        private customerService: CustomerService,
        private applicationContextService: ApplicationContextService
    ) {
        this.customerId = this.route.snapshot.paramMap.get('id') || '';
    }

    ngOnInit(): void {
        // Component will be opened via modal, so we don't load features on init
    }

    openFeatureSelectionModal(): void {
        this.isFeatureModalVisible = true;
        this.loadFeatures();
    }

    loadFeatures(): void {
        this.isLoading = true;

        const features$ = this.featureManagementService.getAllFeatures();
        const selectedApplicationId = this.applicationContextService.getSelectedApplicationId();
        const configParams: any = { id: this.customerId };
        if (selectedApplicationId != null) {
            configParams.app_id = selectedApplicationId;
        }
        const config$ = this.customerService.getCustomerConfig(configParams);

        forkJoin({ features: features$, config: config$ })
            .pipe(untilDestroyed(this))
            .subscribe({
                next: ({ features, config }) => {
                    // Features list
                    this.features = Array.isArray(features?.data) ? features.data : [];

                    // Active feature ids from customer-config where is_active is true
                    this.activeFeatureIds = new Set<number>();
                    if (config?.status === 200 && config?.data) {
                        const configArray = Array.isArray(config.data) ? config.data : [config.data];
                        configArray.forEach((c: any) => {
                            if (c && c.is_active) {
                                const fid = c.feature_id;
                                if (fid != null) {
                                    this.activeFeatureIds.add(fid);
                                }
                            }
                        });
                    }

                    this.isLoading = false;
                },
                error: (err: any) => {
                    console.error('Error fetching features/config:', err);
                    this.isLoading = false;
                    this.commonService.message.error('Failed to fetch features');
                }
            });
    }

    onFeatureSelect(feature: any): void {
        this.selectedFeature = feature;
        this.isFeatureModalVisible = false;
        this.isFeatureFormVisible = true;
    }

    updateFeatureStatus(feature: any, isActive: boolean): void {
        if (!this.customerId || !feature) {
            return;
        }

        const payload = {
            customer_id: this.customerId,
            feature_id: feature.id || feature.feature_id,
            is_active: isActive
        };

        this.customerService.updateCustomerConfigStatus('', payload).pipe(untilDestroyed(this)).subscribe({
            next: (res: any) => {
                if (res.status === 200) {
                    this.commonService.message.success(
                        `Feature "${feature.name}" ${isActive ? 'enabled' : 'disabled'} successfully`
                    );
                    // Optionally reflect status in local list if backend returns it
                    if (res.data && typeof res.data.is_active !== 'undefined') {
                        feature.is_active = res.data.is_active;
                    } else {
                        feature.is_active = isActive;
                    }
                } else {
                    this.commonService.message.error(res.message || 'Failed to update feature status');
                }
            },
            error: (err: any) => {
                console.error('Error updating feature status:', err);
                this.commonService.message.error('Failed to update feature status');
            }
        });
    }

    closeFeatureForm(): void {
        this.isFeatureFormVisible = false;
        this.selectedFeature = null;
    }

    onFeatureFormSubmit(formData: any): void {
        console.log('Feature form submitted:', formData);
        this.commonService.message.success(`Feature "${this.selectedFeature.name}" configured successfully`);
        this.closeFeatureForm();
    }

    trackByFeatureId(index: number, feature: any): any {
        return feature?.id || feature?.feature_id || index;
    }

    isFeatureActive(feature: any): boolean {
        const id = feature?.id ?? feature?.feature_id;
        return id != null && this.activeFeatureIds.has(id);
    }
}

